"use client";

import Image from "next/image";
import Link from "next/link";
import { Clock, LoaderCircle, Search, TrendingUp } from "lucide-react";
import {
  type FormEvent,
  type KeyboardEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type {
  PopularSearchesResponse,
  ProductSearchSuggestion,
  ProductSearchSuggestionsResponse,
  SearchCategorySuggestion,
} from "@/types/search";
import { useClickOutside } from "@/hooks/useClickOutside";
import { useOverlayManager } from "@/hooks/useOverlayManager";
import { useRouteTransition } from "@/hooks/useRouteTransition";
import { getProductHref } from "@/lib/routing/storefrontUrls";
import { highlightParts } from "@/lib/catalog/searchSuggestions";
import { readRecentSearches, saveRecentSearch } from "@/lib/catalog/recentSearches";
import { SearchWhatsAppButton } from "@/components/Search/SearchWhatsAppButton";

interface ProductSearchProps {
  variant: "desktop" | "mobile";
  compact?: boolean;
  /** Termo atual da página /busca (mantém o texto na caixa depois de buscar). */
  initialQuery?: string;
}

type SuggestionsStatus = "idle" | "loading" | "success" | "error";

interface SearchResults {
  products: ProductSearchSuggestion[];
  total: number;
  categories: SearchCategorySuggestion[];
  correctedQuery: string | null;
  related: string[];
}

type SearchOption =
  | { kind: "term"; id: string; term: string; source: "recent" | "popular" }
  | { kind: "category"; id: string; category: SearchCategorySuggestion }
  | { kind: "product"; id: string; product: ProductSearchSuggestion }
  | { kind: "all"; id: string };

const MINIMUM_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 200;
const FALLBACK_IMAGE =
  "/images/brand/persi-materiais-eletricos-e-hidraulicos-ferramentas.webp";
// Até haver buscas reais suficientes, a caixa vazia mostra estas sugestões.
const FALLBACK_SUGGESTIONS = ["disjuntor", "cabo flexível", "torneira", "lâmpada led", "registro", "caixa d'água"];
const EMPTY_RESULTS: SearchResults = { products: [], total: 0, categories: [], correctedQuery: null, related: [] };

let popularSearchesRequest: Promise<string[]> | null = null;

function loadPopularSearches(): Promise<string[]> {
  popularSearchesRequest ??= fetch("/api/search/popular")
    .then((response) => (response.ok ? response.json() : { searches: [] }))
    .then((result: PopularSearchesResponse) => (Array.isArray(result.searches) ? result.searches : []))
    .catch(() => []);
  return popularSearchesRequest;
}

function formatCurrency(value: number, currencyCode: string) {
  try {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currencyCode,
    }).format(value);
  } catch {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  }
}

function Highlighted({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightParts(text, query).map((part, index) =>
        part.match ? (
          <strong key={index} className="font-bold">{part.text}</strong>
        ) : (
          <span key={index} className="font-normal">{part.text}</span>
        ),
      )}
    </>
  );
}

function searchHref(term: string) {
  return `/busca?q=${encodeURIComponent(term)}`;
}

function reportAutocompleteClick(term: string, total: number) {
  try {
    const body = JSON.stringify({ q: term, total });
    if (!navigator.sendBeacon?.("/api/search/click", new Blob([body], { type: "application/json" }))) {
      void fetch("/api/search/click", { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => undefined);
    }
  } catch {
    // Registro é opcional: nunca atrapalha a navegação.
  }
}

export function ProductSearch({
  variant,
  compact = false,
  initialQuery = "",
}: ProductSearchProps) {
  const { navigate } = useRouteTransition();
  const resultsId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(initialQuery);
  const [syncedInitialQuery, setSyncedInitialQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchResults>(EMPTY_RESULTS);
  const [status, setStatus] = useState<SuggestionsStatus>("idle");
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [popularSearches, setPopularSearches] = useState<string[]>([]);
  const trimmedQuery = query.trim();
  const canSuggest = trimmedQuery.length >= MINIMUM_QUERY_LENGTH;
  const showStartPanel = isOpen && trimmedQuery.length === 0;
  const isSearchActive = (isFocused && Boolean(trimmedQuery)) || isOpen;

  // Nova busca na página /busca: a caixa acompanha o termo da URL.
  if (initialQuery !== syncedInitialQuery) {
    setSyncedInitialQuery(initialQuery);
    setQuery(initialQuery);
  }

  useEffect(() => {
    if (!canSuggest || !isFocused) return;

    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      setStatus("loading");
      setIsOpen(true);

      try {
        const response = await fetch(
          `/api/search/suggestions?q=${encodeURIComponent(trimmedQuery)}`,
          { signal: controller.signal },
        );

        if (!response.ok) throw new Error("Search request failed");

        const result =
          (await response.json()) as ProductSearchSuggestionsResponse;
        setResults({
          products: result.products ?? [],
          total: result.total ?? result.products?.length ?? 0,
          categories: result.categories ?? [],
          correctedQuery: result.correctedQuery ?? null,
          related: result.related ?? [],
        });
        setActiveIndex(-1);
        setStatus("success");
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }

        setResults(EMPTY_RESULTS);
        setStatus("error");
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [canSuggest, isFocused, trimmedQuery]);

  function closeSearch() {
    setIsOpen(false);
    setIsFocused(false);
    setActiveIndex(-1);
  }

  useOverlayManager({
    id: `product-search-${resultsId}`,
    isOpen: isSearchActive,
    onClose: closeSearch,
  });
  useClickOutside({
    isOpen: isSearchActive,
    refs: [containerRef],
    onOutside: closeSearch,
  });

  useEffect(() => {
    if (!isSearchActive) return;

    const header =
      containerRef.current?.closest("[data-search-header]") ??
      containerRef.current?.closest("header");
    const overlay = overlayRef.current;
    if (!header || !overlay) return;

    function updateOverlayTop() {
      const currentHeader =
        containerRef.current?.closest("[data-search-header]") ??
        containerRef.current?.closest("header");
      const currentOverlay = overlayRef.current;
      if (!currentHeader || !currentOverlay) return;

      currentOverlay.style.top = `${Math.max(0, currentHeader.getBoundingClientRect().bottom)}px`;
    }

    updateOverlayTop();
    window.addEventListener("resize", updateOverlayTop);
    window.addEventListener("scroll", updateOverlayTop, { passive: true });

    return () => {
      window.removeEventListener("resize", updateOverlayTop);
      window.removeEventListener("scroll", updateOverlayTop);
    };
  }, [isSearchActive]);

  const startTerms: SearchOption[] = [
    ...recentSearches.map((term) => ({ kind: "term" as const, id: `recent-${term}`, term, source: "recent" as const })),
    ...(popularSearches.length > 0 ? popularSearches : FALLBACK_SUGGESTIONS)
      .filter((term) => !recentSearches.includes(term))
      .slice(0, 6)
      .map((term) => ({ kind: "term" as const, id: `popular-${term}`, term, source: "popular" as const })),
  ];
  const resultOptions: SearchOption[] = status === "success" && results.products.length > 0
    ? [
        ...results.categories.map((category) => ({ kind: "category" as const, id: `category-${category.href}`, category })),
        ...results.products.map((product) => ({ kind: "product" as const, id: `product-${product.id}`, product })),
        { kind: "all" as const, id: "all" },
      ]
    : [];
  const options = showStartPanel ? startTerms : isOpen ? resultOptions : [];

  function openStartPanel() {
    setRecentSearches(readRecentSearches());
    setIsOpen(true);
    void loadPopularSearches().then(setPopularSearches);
  }

  function goToSearch(term: string) {
    const cleanTerm = term.trim();
    if (!cleanTerm) return;
    setRecentSearches(saveRecentSearch(cleanTerm));
    setQuery(cleanTerm);
    closeSearch();
    navigate(searchHref(cleanTerm));
  }

  function choose(option: SearchOption) {
    if (option.kind === "term") {
      goToSearch(option.term);
      return;
    }
    if (option.kind === "all") {
      goToSearch(trimmedQuery);
      return;
    }
    saveRecentSearch(trimmedQuery);
    if (option.kind === "product") reportAutocompleteClick(trimmedQuery, results.total);
    closeSearch();
    navigate(option.kind === "category" ? option.category.href : getProductHref(option.product.slug));
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    goToSearch(trimmedQuery);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      closeSearch();
      event.currentTarget.blur();
      return;
    }

    if (options.length === 0) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) => (current + 1) % options.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setActiveIndex((current) =>
        current <= 0 ? options.length - 1 : current - 1,
      );
    } else if (event.key === "Home" && isOpen) {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === "End" && isOpen) {
      event.preventDefault();
      setActiveIndex(options.length - 1);
    } else if (event.key === "Enter" && isOpen && activeIndex >= 0 && options[activeIndex]) {
      event.preventDefault();
      choose(options[activeIndex]);
    }
  }

  function handleQueryChange(value: string) {
    setQuery(value);
    setActiveIndex(-1);

    if (value.trim().length < MINIMUM_QUERY_LENGTH) {
      setResults(EMPTY_RESULTS);
      setStatus("idle");
      if (value.trim().length === 0) openStartPanel();
      else setIsOpen(false);
    }
  }

  const isDesktop = variant === "desktop";
  const isLoading = status === "loading" && isSearchActive;
  const optionId = (option: SearchOption) => `${resultsId}-option-${option.id}`;
  const optionClass = (option: SearchOption) =>
    options[activeIndex]?.id === option.id ? "bg-blue-50 ring-2 ring-inset ring-primary" : "";
  const recentOptions = startTerms.filter((option) => option.kind === "term" && option.source === "recent");
  const popularOptions = startTerms.filter((option) => option.kind === "term" && option.source === "popular");
  const termButtonClass =
    "flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-foreground transition-colors hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary";

  return (
    <div
      ref={containerRef}
      className={
        isDesktop
          ? "relative hidden flex-1 md:block"
          : compact
            ? "relative md:hidden"
            : "relative md:hidden"
      }
    >
      <form
        className={`flex overflow-hidden bg-white ${
          isDesktop ? "h-[42px] rounded-md" : "h-10 rounded-xl"
        }`}
        role="search"
        action="/busca"
        onSubmit={submitSearch}
      >
        <input
          type="search"
          name="q"
          role="combobox"
          value={query}
          onChange={(event) => handleQueryChange(event.target.value)}
          onFocus={() => {
            setIsFocused(true);
            if (canSuggest) setIsOpen(true);
            else if (!trimmedQuery) openStartPanel();
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder={
            isDesktop
              ? "Pesquisar produtos na Persi"
              : compact
                ? "Pesquisar produtos na Persi"
                : "O que você procura?"
          }
          aria-label="Pesquisar produtos"
          aria-autocomplete="list"
          aria-controls={resultsId}
          aria-expanded={isOpen}
          aria-activedescendant={
            isOpen && activeIndex >= 0 && options[activeIndex]
              ? optionId(options[activeIndex])
              : undefined
          }
          enterKeyHint="search"
          autoComplete="off"
          className={
            isDesktop
              ? "min-w-0 flex-1 px-5 py-0 text-sm text-foreground outline-none"
              : "min-w-0 flex-1 px-4 py-2 text-sm text-foreground outline-none"
          }
        />

        <button
          type="submit"
          aria-label={isLoading ? "Pesquisando produtos" : "Pesquisar"}
          className={
            isDesktop
              ? "flex min-w-16 items-center justify-center rounded-lg border-2 border-white bg-secondary px-5 text-white transition-colors hover:bg-secondary-hover active:bg-secondary-hover"
              : "flex min-w-14 items-center justify-center rounded-lg border-2 border-white bg-secondary px-4 text-white transition-colors hover:bg-secondary-hover active:bg-secondary-hover"
          }
        >
          {isLoading ? (
            <LoaderCircle
              size={isDesktop ? 23 : 22}
              className="animate-spin text-white"
              aria-hidden="true"
            />
          ) : (
            <Search size={isDesktop ? 23 : 22} aria-hidden="true" />
          )}
        </button>
      </form>

      {isSearchActive ? (
        <div
          ref={overlayRef}
          className="fixed inset-x-0 bottom-0 z-30 bg-black/60"
          aria-hidden="true"
          onPointerDown={closeSearch}
        />
      ) : null}

      {isOpen ? (
        <div
          id={resultsId}
          className="absolute left-0 right-0 top-full z-40 mt-2 max-h-[min(70vh,560px)] overflow-y-auto rounded-xl border border-slate-200 bg-white text-foreground shadow-xl"
          role="listbox"
          aria-label="Sugestões de busca"
        >
          {showStartPanel ? (
            <div className="py-2">
              {recentOptions.length > 0 ? (
                <p className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted">Buscas recentes</p>
              ) : null}
              {recentOptions.map((option) => option.kind === "term" ? (
                <div key={option.id} id={optionId(option)} role="option" aria-selected={options[activeIndex]?.id === option.id} className={optionClass(option)}>
                  <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => choose(option)} className={termButtonClass}>
                    <Clock size={16} className="shrink-0 text-muted" aria-hidden="true" />
                    {option.term}
                  </button>
                </div>
              ) : null)}
              <p className="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted">
                {popularSearches.length > 0 ? "Mais buscados" : "Sugestões"}
              </p>
              {popularOptions.map((option) => option.kind === "term" ? (
                <div key={option.id} id={optionId(option)} role="option" aria-selected={options[activeIndex]?.id === option.id} className={optionClass(option)}>
                  <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => choose(option)} className={termButtonClass}>
                    <TrendingUp size={16} className="shrink-0 text-muted" aria-hidden="true" />
                    {option.term}
                  </button>
                </div>
              ) : null)}
            </div>
          ) : null}

          {!showStartPanel && status === "loading" ? (
            <p className="px-4 py-3 text-sm text-muted">Pesquisando...</p>
          ) : null}
          {!showStartPanel && status === "error" ? (
            <p className="px-4 py-3 text-sm text-muted">
              Não foi possível pesquisar agora.
            </p>
          ) : null}

          {!showStartPanel && status === "success" && results.products.length === 0 ? (
            <div className="px-4 py-4 text-sm">
              <p className="font-semibold text-heading">Nenhum produto encontrado para “{trimmedQuery}”.</p>
              {results.correctedQuery ? (
                <p className="mt-2 text-foreground">
                  Você quis dizer:{" "}
                  <button
                    type="button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => goToSearch(results.correctedQuery ?? "")}
                    className="font-semibold text-primary underline underline-offset-2"
                  >
                    {results.correctedQuery}
                  </button>
                  ?
                </p>
              ) : (
                <p className="mt-1 text-muted">Confira a escrita ou tente um termo mais simples, como o nome do produto.</p>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <Link
                  href="/loja"
                  onClick={closeSearch}
                  className="inline-flex h-10 items-center rounded-xl border border-slate-200 px-4 font-medium text-foreground hover:bg-slate-50"
                >
                  Ver todas as categorias
                </Link>
                <SearchWhatsAppButton term={trimmedQuery} position="busca_autocomplete_sem_resultado" />
              </div>
            </div>
          ) : null}

          {!showStartPanel && status === "success" && results.products.length > 0 ? (
            <>
              {results.correctedQuery ? (
                <p className="border-b border-slate-100 px-4 py-2 text-xs text-muted">
                  Mostrando resultados para <strong className="text-foreground">{results.correctedQuery}</strong>
                </p>
              ) : null}
              {results.categories.length > 0 ? (
                <ul className="border-b border-slate-100 py-1">
                  {resultOptions.map((option) => option.kind === "category" ? (
                    <li key={option.id} id={optionId(option)} role="option" aria-selected={options[activeIndex]?.id === option.id} className={optionClass(option)}>
                      <Link
                        href={option.category.href}
                        onClick={() => { saveRecentSearch(trimmedQuery); closeSearch(); }}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-foreground transition-colors hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                      >
                        <Search size={16} className="shrink-0 text-muted" aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate">
                          <Highlighted text={option.category.name} query={trimmedQuery} />
                          {option.category.parentName ? (
                            <span className="text-muted"> em {option.category.parentName}</span>
                          ) : null}
                        </span>
                        <span className="shrink-0 text-xs text-muted">{option.category.count}</span>
                      </Link>
                    </li>
                  ) : null)}
                </ul>
              ) : null}
              <ul className="divide-y divide-slate-100">
                {resultOptions.map((option) => option.kind === "product" ? (
                  <li
                    id={optionId(option)}
                    key={option.id}
                    role="option"
                    aria-selected={options[activeIndex]?.id === option.id}
                    className={optionClass(option)}
                  >
                    <Link
                      href={getProductHref(option.product.slug)}
                      onClick={() => {
                        saveRecentSearch(trimmedQuery);
                        reportAutocompleteClick(trimmedQuery, results.total);
                        setIsOpen(false);
                        setIsFocused(false);
                        setActiveIndex(-1);
                      }}
                      className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                    >
                      <Image
                        src={option.product.image?.src || FALLBACK_IMAGE}
                        alt={option.product.image?.alt || option.product.name}
                        width={56}
                        height={56}
                        sizes="56px"
                        className="h-14 w-14 shrink-0 rounded-xl border border-slate-100 object-contain p-1"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="line-clamp-2 block text-sm text-foreground">
                          <Highlighted text={option.product.name} query={results.correctedQuery ?? trimmedQuery} />
                        </span>
                        <span className="mt-1 flex flex-wrap items-center gap-x-2 text-xs">
                          <span className="font-bold text-heading">
                            {formatCurrency(option.product.price, option.product.currencyCode)}
                          </span>
                          <span
                            className={
                              option.product.available
                                ? "text-emerald-700"
                                : "text-red-700"
                            }
                          >
                            {option.product.available ? "Disponível" : "Em falta"}
                          </span>
                        </span>
                      </span>
                    </Link>
                  </li>
                ) : null)}
              </ul>
              {resultOptions.map((option) => option.kind === "all" ? (
                <div key={option.id} id={optionId(option)} role="option" aria-selected={options[activeIndex]?.id === option.id} className={`border-t border-slate-100 ${optionClass(option)}`}>
                  <Link
                    href={searchHref(trimmedQuery)}
                    onClick={(event) => { event.preventDefault(); goToSearch(trimmedQuery); }}
                    className="block px-4 py-3 text-center text-sm font-semibold text-primary hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                  >
                    Ver todos os {results.total} resultados para “{trimmedQuery}”
                  </Link>
                </div>
              ) : null)}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
